import type { PricesResponseMapper } from './pricesResponseMapper'
import type {
  QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { PricesResponse } from '@/scopes/prices/domain/prices/types'
import defaultFetch from 'isomorphic-unfetch'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import filterQueryStringSerializer
  from '@/scopes/prices/infrastructure/filter-query-string-serializer/filterQueryStringSerializer'
import { pricesPassThroughParams } from '@/scopes/prices/types'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { createPricesResponseMapper } from './pricesResponseMapper'

interface FiltersContext {
  accoId: string
  contextItemId: string
  bookingGateId: string
}

type FiltersCriteria = FiltersContext & { filterSelection: FilterSelection }

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }

  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer<FiltersContext & FilterSelection>
  pricesResponseMapper: PricesResponseMapper
  browserQuery: BrowserQueryString
}

export interface PricesApiClient {
  getPrices: (criteria: FiltersCriteria) => Promise<PricesResponse>
}

export function createPricesApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = filterQueryStringSerializer,
    pricesResponseMapper = createPricesResponseMapper(),
    browserQuery = createBrowserQueryString(),
  } = dependencies as Dependencies

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getPrices(criteria: FiltersCriteria) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)

    const data = await response.json()

    return pricesResponseMapper.map(data, criteria.filterSelection)
  }

  function buildAPIUrl({ accoId, contextItemId, bookingGateId, filterSelection }: FiltersCriteria) {
    const filtersContext = {
      accoId,
      bookingGateId,
      contextItemId,
    }

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, pricesPassThroughParams)

    const queryString = queryStringSerializer.serialize({
      ...filtersContext,
      ...filterSelection,
      ...passThroughParams,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingGate/GetPricesGroupedByDurationApi?${queryString}`
  }

  return { getPrices }
}
